/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * The internal dependencies
 */
import { Outlet, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import NavigationBar from '../components/NavigationBar/NavigationBar';
import { useStorex } from '../store/UIStore';
import { useChangeStore } from '../store/changeStore';
import {
  useChangeApiFactory,
  usePspPhaseApiFactory,
  useUserApiFactory,
  useWorkMaterialApiFactory,
  useWorkPackageApiFactory,
  useWorkServiceApiFactory,
} from '../ApiClient';
import { useUserStore } from '../store/userStore';
import { useProjectStore } from '../store/projectStore';
import PageNotFoundText from '../components/Error/PageNotFoundText';
import useMedia from '../hooks/useMedia';

const ChangeLayoutPage = observer(() => {
  const { t } = useTranslation();
  const pageTitles: { [key: string]: string } = {
    edit: t('Change'),
    resources: t('navigation.resources'),
    calculation: t('navigation.calculation'),
    approval: t('navigation.approvalStep'),
  };

  const store = useStorex();
  const {
    change,
    setChange,
    setWorkPackages,
    setIsDataLoading,
    setWorkMaterials,
    setWorkServices,
  } = useChangeStore();
  const { setUsers } = useUserStore();
  const { setPsPPhases } = useProjectStore();
  const { changeId } = useParams();
  const location = useLocation();

  const isMobile = useMedia('sm');

  const changeApi = useChangeApiFactory();
  const userApi = useUserApiFactory();
  const pspPhaseApi = usePspPhaseApiFactory();
  const useWorkPackageApi = useWorkPackageApiFactory();
  const useWorkServiceApi = useWorkServiceApiFactory();
  const useWorkMaterialApi = useWorkMaterialApiFactory();
  const [permissionDenied, setPermissionDenied] = useState(false);

  const pageName: string = location.pathname.split('/').pop() ?? '';

  useEffect(() => {
    if (changeId) {
      (async () => {
        try {
          setIsDataLoading(true);

          const { data: newChange } = await changeApi.changesControllerFindOne({
            id: Number(changeId),
          });

          if (newChange) {
            setIsDataLoading(false);
            setChange(newChange);
          }
        } catch (e: any) {
          setIsDataLoading(false);
          if (e?.response?.data?.statusCode === 404) {
            setPermissionDenied(true);
            console.log('permission denied');
          }
          console.log(e);
        }

        try {
          setIsDataLoading(true);
          const { data: workPackages } =
            await useWorkPackageApi.workPackagesControllerFindAll({
              changeIds: [Number(changeId)],
            });

          if (workPackages) {
            setIsDataLoading(false);
            setWorkPackages(workPackages);
          }
        } catch (e) {
          setIsDataLoading(false);
          console.log(e);
        }
        try {
          setIsDataLoading(true);
          const { data: workServices } =
            await useWorkServiceApi.workServicesControllerFindAll({
              changeIds: [Number(changeId)],
            });

          if (workServices) {
            setIsDataLoading(false);
            setWorkServices(workServices);
          }
        } catch (e) {
          setIsDataLoading(false);
          console.log(e);
        }
        try {
          setIsDataLoading(true);
          const { data: workMaterials } =
            await useWorkMaterialApi.workMaterialsControllerFindAll({
              changeIds: [Number(changeId)],
            });

          if (workMaterials) {
            setIsDataLoading(false);
            setWorkMaterials(workMaterials);
          }
        } catch (e) {
          setIsDataLoading(false);
          console.log(e);
        }
      })();
    }
  }, [
    changeApi,
    changeId,
    setChange,
    setIsDataLoading,
    setWorkPackages,
    useWorkPackageApi,
    setWorkMaterials,
    useWorkMaterialApi,
    setWorkServices,
    useWorkServiceApi,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const { data: allUsers } = await userApi.usersControllerFindAll();
        if (allUsers) {
          setUsers(allUsers);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, [setUsers, userApi]);

  useEffect(() => {
    (async () => {
      try {
        // 1. Fetch all phases
        const { data: phases } = await pspPhaseApi.pspPhasesControllerFindAll();
        setPsPPhases(phases);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [pspPhaseApi, setPsPPhases]);

  return (
    <Box
      css={css`
        padding: 48px 15px 24px;
        flex-grow: 1;
        margin-left: auto;
        transition: width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
        width: calc(100% - ${store.sidebar ? 236 : 0}px);

        @media (max-width: 900px) {
          width: 100%;
        }

        @media (max-width: 375px) {
          padding: 48px 16px 24px;
        }
      `}
    >
      <NavigationBar HasSidebar />
      {!isMobile && (
        <Breadcrumbs
          separator=">"
          css={css`
            margin-top: -48px;
            font-size: 13px;
            padding: 12px;
            font-weight: 600;
          `}
        >
          <Link href="/projects" underline="hover">
            {t('project.projects')}
          </Link>
          {change && change.commonProject ? (
            <Link
              href={`/projects/${change.commonProject.id}`}
              underline="hover"
            >
              {change.commonProject.number}
            </Link>
          ) : null}
          {change && change.id ? (
            <Link href={`/change-request/${change.id}`} underline="hover">
              {pageTitles[pageName] || t('overview')}
            </Link>
          ) : null}
          <Link href={`/change-request/${change?.id}`} underline="hover">
            {change?.title}
          </Link>
        </Breadcrumbs>
      )}

      {!permissionDenied ? <Outlet /> : <PageNotFoundText />}
    </Box>
  );
});

export default ChangeLayoutPage;
